// CRM Client Profile: everything Sonar knows and believes about one client, in
// one place. Details (same fields as the Clients edit modal), the discovery
// brief (feeds sequence copy), the ICP (the active version drives market runs
// and fit scoring), and Find companies (ICP-driven prospecting). Raptor sits on
// the right: it drafts, the operator reviews and saves; nothing is auto-saved.
// Reached from the Clients screen (operator shell) and from inside a workspace.
import { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '@clerk/clerk-react';
import toast from 'react-hot-toast';
import MDEditor from '@uiw/react-md-editor';
import API_BASE from '../../config';
import CrmSequenceSettings from './CrmSequenceSettings';
import pricing from '../../data/pricing';
import { creditAwareFetch } from '../../hooks/creditAwareFetch';
import raptorIcon from '../../assets/raptor.svg';
import raptorMark from '../../assets/raptor-white.svg';

const SECTORS = [
  'Technology, Information and Media', 'Financial Services', 'Hospitals and Health Care',
  'Manufacturing', 'Professional Services', 'Consumer Services', 'Retail', 'Education',
  'Real Estate and Equipment Rental', 'Construction', 'Transportation, Logistics, Supply Chain and Storage',
  'Energy, Mining and Utilities', 'Government Administration', 'Non-profit Organizations',
  'Entertainment Providers', 'Accommodation and Food Services', 'Administrative and Support Services',
  'Farming, Ranching and Forestry', 'Civic and Social Organizations', 'International Affairs',
  'Public Safety', 'Holding Companies', 'Religious Institutions', 'Other'
];
// Exactly the fields the Clients edit modal sends, so a save here behaves the same.
const DETAIL_KEYS = ['name', 'sector', 'website', 'contact_name', 'contact_email', 'service_track', 'service_tier', 'notes', 'account_id', 'cta_url', 'landing_page_url', 'attachment_url', 'attachment_name'];

const DISC_FIELDS = [
  { key: 'best_clients', label: 'Best clients', hint: 'Who they love working with and what makes a great fit.' },
  { key: 'worst_clients', label: 'Worst clients', hint: 'The wrong clients, and what makes a bad fit.' },
  { key: 'previous_attempts', label: 'Previous outreach', hint: 'What they have tried before, and what worked.' },
  { key: 'sales_cycle', label: 'Sales cycle', hint: 'Typical time from first contact to close, and the stages.' },
  { key: 'sensitivities', label: 'Sensitivities', hint: 'Topics, sectors or approaches to avoid.' },
  { key: 'tone_preferences', label: 'Tone', hint: 'How they want to come across: formal, conversational, direct.' }
];
const ICP_FIELDS = [
  { key: 'sector', label: 'Sector' },
  { key: 'company_size', label: 'Company size' },
  { key: 'geography', label: 'Geography' },
  { key: 'decision_maker_titles', label: 'Decision makers' },
  { key: 'buying_triggers', label: 'Buying triggers', wide: true },
  { key: 'disqualifiers', label: 'Disqualifiers', wide: true }
];
const PROMPT_TYPE = 'ICP Generation';
const SIZE_PILLS = ['1-10', '10-50', '50-200', '200-500', '500+'];
const SECTIONS = [
  { key: 'details', label: 'Details' },
  { key: 'discovery', label: 'Discovery' },
  { key: 'icp', label: 'ICP' },
  { key: 'find', label: 'Find companies' }
];

const detailsFrom = c => { const f = {}; DETAIL_KEYS.forEach(k => { f[k] = c && c[k] != null ? c[k] : ''; }); return f; };
const str = v => (v == null ? '' : Array.isArray(v) ? v.join(', ') : String(v));
const fmtDate = ts => (ts ? new Date(ts).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }) : '');

function Favicon({ website, name }) {
  const [failed, setFailed] = useState(false);
  let domain = '';
  try { domain = website ? new URL(website.startsWith('http') ? website : 'https://' + website).hostname : ''; } catch (_) {}
  const initials = (name || '?').split(' ').map(w => w[0]).filter(Boolean).slice(0, 2).join('').toUpperCase();
  if (!domain || failed) return <div className="crmcl-fav crmcl-fav--fb" style={{ width: 36, height: 36 }}>{initials}</div>;
  return <div className="crmcl-fav" style={{ width: 36, height: 36 }}><img alt="" src={`https://www.google.com/s2/favicons?domain=${domain}&sz=64`} onError={() => setFailed(true)} /></div>;
}

function DirtyBar({ count, label, onDiscard, onSave, busy, note }) {
  if (!count) return null;
  return (
    <div className="crmcp-dirty">
      <span className="crmcp-dirty-txt">{count} unsaved {count === 1 ? 'change' : 'changes'}{note ? <span className="crmcp-dirty-note"> · {note}</span> : null}</span>
      <button className="crmcl-act" onClick={onDiscard} disabled={busy}>Discard</button>
      <button className="crmcl-new" onClick={onSave} disabled={busy}>{busy ? 'Saving...' : label}</button>
    </div>
  );
}

// Auto-growing textarea so long answers are readable without an edit mode.
function Grow({ value, onChange, placeholder, rows = 2 }) {
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current;
    if (el) { el.style.height = 'auto'; el.style.height = Math.min(el.scrollHeight, 420) + 'px'; }
  }, [value]);
  return <textarea ref={ref} className="crmcp-ta" rows={rows} value={value} placeholder={placeholder} onChange={e => onChange(e.target.value)} />;
}

export default function CrmClientProfile() {
  const params = useParams();
  const cid = params.clientId || params.id;
  const inWorkspace = !params.clientId;
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const section = SECTIONS.some(s => s.key === searchParams.get('s')) ? searchParams.get('s') : 'icp';
  const setSection = s => setSearchParams(prev => { const n = new URLSearchParams(prev); n.set('s', s); return n; }, { replace: true });
  const { getToken } = useAuth();

  const authed = useCallback(async (path, opts = {}) => {
    const token = await getToken();
    return fetch(`${API_BASE}${path}`, { ...opts, headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}`, ...(opts.headers || {}) } });
  }, [getToken]);

  const [client, setClient] = useState(null);
  const [accounts, setAccounts] = useState([]);
  const [discovery, setDiscovery] = useState(null);
  const [icp, setIcp] = useState(null);
  const [versions, setVersions] = useState([]);
  const [evidence, setEvidence] = useState([]);

  const [detForm, setDetForm] = useState({});
  const [discDraft, setDiscDraft] = useState({});
  const [discRaptor, setDiscRaptor] = useState({});
  const [icpDraft, setIcpDraft] = useState({});
  const [icpRaptor, setIcpRaptor] = useState({});
  const [icpPromptUsed, setIcpPromptUsed] = useState('');
  const [saving, setSaving] = useState(null);
  const [confirm, setConfirm] = useState(null);

  const [icpInstr, setIcpInstr] = useState('');
  const [discDesc, setDiscDesc] = useState('');
  const [drafting, setDrafting] = useState(null);
  const [prompts, setPrompts] = useState([]);
  const [promptsOpen, setPromptsOpen] = useState(false);
  const [promptForm, setPromptForm] = useState(null);
  const [askInput, setAskInput] = useState('');
  const [messages, setMessages] = useState([]);
  const [asking, setAsking] = useState(false);

  const [filters, setFilters] = useState({ sector: '', company_size: '', geography: '', include_keywords: '', exclude_keywords: '' });
  const [filtersSeeded, setFiltersSeeded] = useState(false);
  const [results, setResults] = useState(null);
  const [selected, setSelected] = useState(() => new Set());
  const [searching, setSearching] = useState(false);
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);

  const loadClient = useCallback(async () => {
    try {
      const r = await fetch(`${API_BASE}/clients/${cid}`);
      if (r.ok) { const c = await r.json(); setClient(c); setDetForm(detailsFrom(c)); }
    } catch (_) {}
  }, [cid]);
  const loadDiscovery = useCallback(async () => {
    try { const r = await fetch(`${API_BASE}/clients/${cid}/discovery`); if (r.ok) setDiscovery((await r.json()) || {}); } catch (_) {}
  }, [cid]);
  const loadIcp = useCallback(async () => {
    try { const r = await fetch(`${API_BASE}/clients/${cid}/icp`); if (r.ok) setIcp((await r.json()) || null); } catch (_) {}
  }, [cid]);
  const loadVersions = useCallback(async () => {
    try { const r = await authed(`/clients/${cid}/icp/versions`); if (r.ok) setVersions((await r.json()).versions || []); } catch (_) {}
  }, [authed, cid]);
  const loadEvidence = useCallback(async () => {
    try { const r = await authed(`/clients/${cid}/profile/evidence`); if (r.ok) setEvidence((await r.json()).offerings || []); } catch (_) {}
  }, [authed, cid]);
  const loadAccounts = useCallback(async () => {
    try { const r = await authed('/accounts'); if (r.ok) { const d = await r.json(); setAccounts(Array.isArray(d) ? d : (d.accounts || [])); } } catch (_) {}
  }, [authed]);
  // Saved ICP-generation prompts, shared across clients (prompt_library).
  const loadPrompts = useCallback(async () => {
    try {
      const r = await fetch(`${API_BASE}/prompts`);
      if (r.ok) { const d = await r.json(); setPrompts((Array.isArray(d) ? d : []).filter(x => x.prompt_type === PROMPT_TYPE)); }
    } catch (_) {}
  }, []);

  useEffect(() => {
    setDiscDraft({}); setIcpDraft({}); setDiscRaptor({}); setIcpRaptor({}); setResults(null); setSelected(new Set()); setMessages([]); setFiltersSeeded(false);
    loadClient(); loadDiscovery(); loadIcp(); loadVersions(); loadEvidence(); loadAccounts(); loadPrompts();
  }, [cid, loadClient, loadDiscovery, loadIcp, loadVersions, loadEvidence, loadAccounts, loadPrompts]);

  useEffect(() => {
    if (icp && !filtersSeeded) {
      setFilters({ sector: str(icp.sector), company_size: str(icp.company_size), geography: str(icp.geography), include_keywords: '', exclude_keywords: '' });
      setFiltersSeeded(true);
    }
  }, [icp, filtersSeeded]);

  // Merged values and dirty sets.
  const discVal = k => (k in discDraft ? discDraft[k] : str(discovery && discovery[k]));
  const icpVal = k => (k in icpDraft ? icpDraft[k] : str(icp && icp[k]));
  const discDirty = DISC_FIELDS.filter(f => f.key in discDraft && discDraft[f.key] !== str(discovery && discovery[f.key])).map(f => f.key);
  const icpDirty = ICP_FIELDS.filter(f => f.key in icpDraft && icpDraft[f.key] !== str(icp && icp[f.key])).map(f => f.key);
  const detDirty = DETAIL_KEYS.filter(k => str(detForm[k]) !== str(client && client[k]));
  const discFilled = DISC_FIELDS.filter(f => str(discovery && discovery[f.key]).trim()).length;
  const nextVersion = (versions[0] ? versions[0].version : (icp ? icp.version : 0)) + 1;

  async function saveDiscovery() {
    setSaving('discovery');
    try {
      const body = {}; DISC_FIELDS.forEach(f => { body[f.key] = discVal(f.key); });
      const r = await fetch(`${API_BASE}/clients/${cid}/discovery`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) });
      if (!r.ok) throw new Error('save failed (' + r.status + ')');
      await loadDiscovery(); setDiscDraft({}); setDiscRaptor({});
      toast.success('Discovery brief saved');
    } catch (e) { toast.error('Could not save the brief: ' + e.message); }
    setSaving(null);
  }

  async function saveIcp(fields, promptUsed, successMsg) {
    const r = await fetch(`${API_BASE}/clients/${cid}/icp`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ ...fields, prompt_used: promptUsed || '' })
    });
    if (!r.ok) throw new Error('save failed (' + r.status + ')');
    const saved = await r.json();
    await Promise.all([loadIcp(), loadVersions()]);
    toast.success(successMsg || ('Saved as version ' + saved.version));
    return saved;
  }

  async function saveIcpDraft() {
    setSaving('icp');
    try {
      const fields = {}; ICP_FIELDS.forEach(f => { fields[f.key] = icpVal(f.key); });
      await saveIcp(fields, icpPromptUsed || 'edited in client profile');
      setIcpDraft({}); setIcpRaptor({}); setIcpPromptUsed('');
    } catch (e) { toast.error('Could not save the ICP: ' + e.message); }
    setSaving(null);
  }

  async function restoreVersion(v) {
    setSaving('restore');
    try {
      const fields = {}; ICP_FIELDS.forEach(f => { fields[f.key] = str(v[f.key]); });
      await saveIcp(fields, 'restored from version ' + v.version, `Version ${v.version} restored as version ${nextVersion}`);
      setIcpDraft({}); setIcpRaptor({});
    } catch (e) { toast.error('Could not restore: ' + e.message); }
    setSaving(null); setConfirm(null);
  }

  async function saveDetails() {
    if (!str(detForm.name).trim()) { toast.error('Client name is required'); return; }
    if (!detForm.account_id) { toast.error('Pick an account for this client'); return; }
    setSaving('details');
    try {
      const r = await authed(`/clients/${cid}`, { method: 'PUT', body: JSON.stringify(detailsFrom(detForm)) });
      if (!r.ok) { const d = await r.json().catch(() => ({})); throw new Error(d.error || ('save failed (' + r.status + ')')); }
      await loadClient();
      toast.success('Client details saved');
    } catch (e) { toast.error('Could not save details: ' + e.message); }
    setSaving(null);
  }

  function applyPrompt(p) {
    setIcpInstr(prev => (prev ? prev + '\n' + p.prompt_text : p.prompt_text));
    setPromptsOpen(false);
    fetch(`${API_BASE}/prompts/${p.id}/use`, { method: 'POST' }).catch(() => {});
    setPrompts(prev => prev.map(x => x.id === p.id ? { ...x, use_count: (x.use_count || 0) + 1 } : x));
  }

  async function savePrompt() {
    const f = promptForm;
    if (!f.title.trim() || !f.prompt_text.trim()) { toast.error('A title and the prompt text are both needed'); return; }
    setSaving('prompt');
    try {
      const r = await fetch(`${API_BASE}/prompts`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ title: f.title.trim(), prompt_text: f.prompt_text.trim(), prompt_type: PROMPT_TYPE, sector_tag: f.sector_tag.trim() || null, performance_notes: null })
      });
      if (!r.ok) throw new Error('save failed (' + r.status + ')');
      await loadPrompts();
      setPromptForm(null);
      toast.success('Prompt saved. It is available for every client.');
    } catch (e) { toast.error('Could not save the prompt: ' + e.message); }
    setSaving(null);
  }

  async function deletePrompt(p) {
    setSaving('prompt');
    try {
      const r = await fetch(`${API_BASE}/prompts/${p.id}`, { method: 'DELETE' });
      if (!r.ok) throw new Error('delete failed (' + r.status + ')');
      await loadPrompts();
      toast.success('Prompt deleted');
    } catch (e) { toast.error('Could not delete: ' + e.message); }
    setSaving(null); setConfirm(null);
  }

  async function draftIcp() {
    setDrafting('icp');
    try {
      const res = await creditAwareFetch(`${API_BASE}/clients/${cid}/generate-icp`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ prompt: icpInstr })
      });
      if (!res) { setDrafting(null); return; }
      const data = await res.json();
      if (!res.ok || data.error) throw new Error(data.error || ('failed (' + res.status + ')'));
      const d = {}, src = {};
      ICP_FIELDS.forEach(f => { if (data[f.key] != null && str(data[f.key]) !== str(icp && icp[f.key])) { d[f.key] = str(data[f.key]); src[f.key] = true; } });
      setIcpDraft(prev => ({ ...prev, ...d })); setIcpRaptor(prev => ({ ...prev, ...src }));
      setIcpPromptUsed(icpInstr || 'Raptor draft from discovery');
      setSection('icp');
      toast.success(Object.keys(d).length ? 'Raptor drafted the ICP. Review the highlighted fields, then save.' : 'Raptor found nothing to change in the current ICP.');
    } catch (e) { toast.error('Raptor could not draft the ICP: ' + e.message); }
    setDrafting(null);
  }

  async function fillDiscovery() {
    if (!discDesc.trim()) return;
    setDrafting('discovery');
    try {
      const res = await creditAwareFetch(`${API_BASE}/clients/${cid}/discovery/generate`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ prompt: discDesc })
      });
      if (!res) { setDrafting(null); return; }
      const data = await res.json();
      if (!res.ok || data.error) throw new Error(data.error || ('failed (' + res.status + ')'));
      const d = {}, src = {};
      DISC_FIELDS.forEach(f => { if (str(data[f.key]).trim()) { d[f.key] = str(data[f.key]); src[f.key] = true; } });
      setDiscDraft(prev => ({ ...prev, ...d })); setDiscRaptor(prev => ({ ...prev, ...src }));
      setDiscDesc(''); setSection('discovery');
      toast.success('Raptor filled the brief. Review the highlighted fields, then save.');
    } catch (e) { toast.error('Raptor could not fill the brief: ' + e.message); }
    setDrafting(null);
  }

  async function ask() {
    const q = askInput.trim();
    if (!q || asking) return;
    const history = messages.slice(-6).map(m => ({ role: m.role, content: m.text }));
    setMessages(prev => [...prev, { role: 'user', text: q }]);
    setAskInput(''); setAsking(true);
    try {
      const r = await authed(`/clients/${cid}/profile/ask`, { method: 'POST', body: JSON.stringify({ message: q, history }) });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(d.error || ('failed (' + r.status + ')'));
      setMessages(prev => [...prev, { role: 'assistant', text: d.reply || '(no answer)' }]);
    } catch (e) {
      setMessages(prev => [...prev, { role: 'assistant', text: 'I could not answer that: ' + e.message, error: true }]);
    }
    setAsking(false);
  }

  async function findCompanies(more) {
    setSearching(true);
    if (!more) { setResults(null); setImportResult(null); setSelected(new Set()); }
    try {
      const res = await creditAwareFetch(`${API_BASE}/clients/${cid}/icp-company-search`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ ...filters, search_more: !!more })
      });
      if (!res) { setSearching(false); return; }
      const data = await res.json();
      if (data.error) throw new Error(data.error);
      setResults(prev => (more && prev) ? { ...data, companies: [...prev.companies, ...(data.companies || [])] } : { ...data, companies: data.companies || [] });
    } catch (e) { toast.error('Could not find companies: ' + e.message); }
    setSearching(false);
  }

  async function clearSearch() {
    setConfirm(null);
    try {
      const r = await fetch(`${API_BASE}/clients/${cid}/icp-search-cache/clear`, { method: 'POST' });
      const d = await r.json();
      if (d.error) throw new Error(d.error);
      setResults(null); setSelected(new Set()); setImportResult(null);
      toast.success('Cleared ' + (d.cleared || 0) + ' cached results. Ready for a fresh search.');
    } catch (e) { toast.error('Could not clear: ' + e.message); }
  }

  async function importSelected() {
    if (!selected.size) return;
    if (selected.size > 10) { toast.error('Up to 10 per batch. You have ' + selected.size + ' selected.'); return; }
    setImporting(true);
    const tid = toast.loading(`Adding and enriching ${selected.size} ${selected.size === 1 ? 'company' : 'companies'}. This can take up to a minute.`);
    try {
      const companies = results.companies.filter(c => selected.has(c.domain));
      const res = await creditAwareFetch(`${API_BASE}/clients/${cid}/icp-company-import`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ companies })
      });
      if (!res) { toast.dismiss(tid); setImporting(false); return; }
      const data = await res.json();
      if (data.error) throw new Error(data.error);
      setImportResult(data);
      toast.success(`${data.added || 0} added to the workspace${data.skipped ? `, ${data.skipped} already there` : ''}`, { id: tid });
      setResults(prev => ({ ...prev, companies: prev.companies.map(c => selected.has(c.domain) ? { ...c, already_in_workspace: true } : c) }));
      setSelected(new Set());
    } catch (e) { toast.error('Could not import: ' + e.message, { id: tid }); }
    setImporting(false);
  }

  function toggle(domain) {
    setSelected(s => { const n = new Set(s); if (n.has(domain)) n.delete(domain); else n.add(domain); return n; });
  }

  const clientsPath = inWorkspace ? `/clients/${cid}/crm/clients` : '/crm/clients';
  const statusVal = (client && client.status) || 'active';
  const ev = evidence;
  const icpEmpty = !icp;

  return (
    <div className="crmcl crmcp">
      <div className="crmcp-top">
        <div className="crmcp-crumbs">
          <button className="crmcp-crumb" onClick={() => navigate(clientsPath)}>Clients</button>
          <span className="crmcp-crumb-sep">/</span>
          <span className="crmcp-crumb-now">{client ? client.name : 'Client'}</span>
        </div>
        {!inWorkspace && <button className="crmcl-act crmcp-open" onClick={() => navigate(`/clients/${cid}/crm/home`)}>Open workspace</button>}
      </div>

      <div className="crmcp-grid">
        {/* LEFT: identity, sections, ICP versions */}
        <aside className="crmcp-col">
          <div className="crmcp-card crmcp-id">
            <div className="crmcp-id-row">
              <Favicon website={client && client.website} name={client && client.name} />
              <div className="crmcp-id-main">
                <div className="crmcp-id-name">{client ? client.name : '...'}</div>
                <div className="crmcp-id-meta">{(client && client.sector) || 'No sector'}</div>
              </div>
            </div>
            <div className="crmcp-id-tags">
              {client && client.account_name && <span className="crmcl-chip crmcl-chip--acct">{client.account_name}</span>}
              <span className={'crmcl-status crmcl-status--' + statusVal}>{statusVal}</span>
            </div>
          </div>

          <div className="crmcp-card crmcp-nav">
            {SECTIONS.map(s => {
              const meta = s.key === 'details' ? (detDirty.length ? 'edited' : '')
                : s.key === 'discovery' ? `${discFilled}/6`
                : s.key === 'icp' ? (icp ? 'v' + icp.version : 'none')
                : '';
              const warn = (s.key === 'discovery' && discFilled < 6) || (s.key === 'icp' && !icp);
              const dirty = (s.key === 'discovery' && discDirty.length) || (s.key === 'icp' && icpDirty.length) || (s.key === 'details' && detDirty.length);
              return (
                <button key={s.key} className={'crmcp-nav-item' + (section === s.key ? ' crmcp-nav-item--on' : '')} onClick={() => setSection(s.key)}>
                  <span>{s.label}{dirty ? <span className="crmcp-dot" title="Unsaved changes" /> : null}</span>
                  <span className={'crmcp-nav-meta' + (warn && section !== s.key ? ' crmcp-nav-meta--warn' : '')}>{meta}</span>
                </button>
              );
            })}
          </div>

          <div className="crmcp-card">
            <div className="crmcp-h">ICP versions</div>
            {versions.length === 0 ? <div className="crmcp-muted">No ICP saved yet.</div> : (
              <>
                {versions.slice(0, 8).map(v => (
                  <div key={v.id} className="crmcp-ver">
                    <span><b>v{v.version}</b> · {fmtDate(v.created_at)}</span>
                    {v.is_active
                      ? <span className="crmcp-pill crmcp-pill--ok">Active</span>
                      : <button className="crmcp-link" disabled={!!saving} onClick={() => setConfirm({ type: 'restore', v })}>Restore</button>}
                  </div>
                ))}
                {versions.length > 8 && <div className="crmcp-muted">{versions.length - 8} older versions</div>}
              </>
            )}
          </div>
        </aside>

        {/* CENTRE: the section being worked on */}
        <main className="crmcp-card crmcp-main">
          {section === 'details' && (
            <>
              <div className="crmcp-sec-head"><div><div className="crmcp-title">Details</div><div className="crmcp-sub">The same fields as the Clients edit form.</div></div></div>
              <DirtyBar count={detDirty.length} label="Save details" busy={saving === 'details'} onDiscard={() => setDetForm(detailsFrom(client))} onSave={saveDetails} />
              <div className="crmcl-grid">
                <div className="crmcl-field crmcl-col2"><label>Client name *</label><input value={detForm.name || ''} onChange={e => setDetForm({ ...detForm, name: e.target.value })} /></div>
                <div className="crmcl-field"><label>Sector</label>
                  <select value={detForm.sector || ''} onChange={e => setDetForm({ ...detForm, sector: e.target.value })}>
                    <option value="">Select sector</option>
                    {detForm.sector && !SECTORS.includes(detForm.sector) && <option value={detForm.sector}>{detForm.sector}</option>}
                    {SECTORS.map(s => <option key={s} value={s}>{s}</option>)}
                  </select></div>
                <div className="crmcl-field"><label>Website</label><input value={detForm.website || ''} onChange={e => setDetForm({ ...detForm, website: e.target.value })} /></div>
                <div className="crmcl-field"><label>Contact name</label><input value={detForm.contact_name || ''} onChange={e => setDetForm({ ...detForm, contact_name: e.target.value })} /></div>
                <div className="crmcl-field"><label>Contact email</label><input value={detForm.contact_email || ''} onChange={e => setDetForm({ ...detForm, contact_email: e.target.value })} /></div>
                <div className="crmcl-field"><label>Account</label>
                  <select value={detForm.account_id || ''} onChange={e => setDetForm({ ...detForm, account_id: e.target.value })}>
                    <option value="" disabled>Select an account</option>
                    {accounts.map(a => <option key={a.id} value={a.id}>{a.name}</option>)}
                  </select></div>
                {(() => {
                  const acc = accounts.find(a => String(a.id) === String(detForm.account_id));
                  const track = acc && acc.plan_track;
                  const tier = acc && pricing[track] && pricing[track].tiers && pricing[track].tiers[acc.plan_tier];
                  return (<>
                    <div className="crmcl-field"><label>Plan track</label><input readOnly value={track ? ((pricing[track] && pricing[track].label) || track) : 'Set on the account'} /></div>
                    <div className="crmcl-field"><label>Plan tier</label><input readOnly value={tier ? `${tier.name}${tier.label ? ` (${tier.label})` : ''}` : ((acc && acc.plan_tier) || 'Set on the account')} /></div>
                  </>);
                })()}
                <div className="crmcl-field crmcl-col2"><label>Notes</label><textarea rows={2} value={detForm.notes || ''} onChange={e => setDetForm({ ...detForm, notes: e.target.value })} /></div>
                <div className="crmcl-field"><label>CTA URL</label><input value={detForm.cta_url || ''} onChange={e => setDetForm({ ...detForm, cta_url: e.target.value })} /></div>
                <div className="crmcl-field"><label>Landing page URL</label><input value={detForm.landing_page_url || ''} onChange={e => setDetForm({ ...detForm, landing_page_url: e.target.value })} /></div>
                <div className="crmcl-field"><label>Attachment URL</label><input value={detForm.attachment_url || ''} onChange={e => setDetForm({ ...detForm, attachment_url: e.target.value })} /></div>
                <div className="crmcl-field"><label>Attachment name</label><input value={detForm.attachment_name || ''} onChange={e => setDetForm({ ...detForm, attachment_name: e.target.value })} /></div>
              </div>
              <div style={{ marginTop: 16 }}><CrmSequenceSettings level="client" id={cid} /></div>
            </>
          )}

          {section === 'discovery' && (
            <>
              <div className="crmcp-sec-head"><div><div className="crmcp-title">Discovery brief</div><div className="crmcp-sub">Feeds every generated sequence: fit, tone and what to avoid.</div></div></div>
              <DirtyBar count={discDirty.length} label="Save brief" busy={saving === 'discovery'} onDiscard={() => { setDiscDraft({}); setDiscRaptor({}); }} onSave={saveDiscovery} />
              <div className="crmcp-fields">
                {DISC_FIELDS.map(f => {
                  const dirty = discDirty.includes(f.key);
                  return (
                    <div key={f.key} className={'crmcp-field' + (dirty ? ' crmcp-field--dirty' : '')}>
                      <div className="crmcp-field-l">{f.label}{dirty && <span className="crmcp-tag">{discRaptor[f.key] ? 'Raptor draft' : 'edited'}</span>}</div>
                      <Grow value={discVal(f.key)} placeholder={f.hint} onChange={v => setDiscDraft(prev => ({ ...prev, [f.key]: v }))} />
                    </div>
                  );
                })}
              </div>
            </>
          )}

          {section === 'icp' && (
            <>
              <div className="crmcp-sec-head">
                <div>
                  <div className="crmcp-title">Ideal customer profile</div>
                  <div className="crmcp-sub">{icp ? `Version ${icp.version} is active and drives market runs and fit scoring.` : 'No ICP yet. Ask Raptor to draft one from the discovery brief.'}</div>
                </div>
              </div>
              <DirtyBar count={icpDirty.length} label={'Save as version ' + nextVersion} busy={saving === 'icp'} note="market runs will use the new version"
                onDiscard={() => { setIcpDraft({}); setIcpRaptor({}); setIcpPromptUsed(''); }} onSave={saveIcpDraft} />
              <div className="crmcp-icp">
                {ICP_FIELDS.map(f => {
                  const dirty = icpDirty.includes(f.key);
                  return (
                    <div key={f.key} className={'crmcp-field' + (f.wide ? ' crmcp-wide' : '') + (dirty ? ' crmcp-field--dirty' : '')}>
                      <div className="crmcp-field-l">{f.label}{dirty && <span className="crmcp-tag">{icpRaptor[f.key] ? 'Raptor draft' : 'edited'}</span>}</div>
                      <Grow value={icpVal(f.key)} placeholder={icpEmpty ? 'Not defined yet' : ''} onChange={v => setIcpDraft(prev => ({ ...prev, [f.key]: v }))} />
                    </div>
                  );
                })}
              </div>
            </>
          )}

          {section === 'find' && (
            <>
              <div className="crmcp-sec-head">
                <div><div className="crmcp-title">Find companies</div><div className="crmcp-sub">Search for companies matching the ICP, then add up to 10 at a time to this workspace.</div></div>
              </div>
              {!icp ? <div className="crmcp-empty">Save an ICP first. The search is seeded from it.</div> : (
                <>
                  <div className="crmcp-filters">
                    <div className="crmcl-field"><label>Sector</label><input value={filters.sector} onChange={e => setFilters(p => ({ ...p, sector: e.target.value }))} /></div>
                    <div className="crmcl-field"><label>Geography</label><input value={filters.geography} onChange={e => setFilters(p => ({ ...p, geography: e.target.value }))} /></div>
                    <div className="crmcl-field crmcl-col2"><label>Company size</label>
                      <div className="crmcp-pills">
                        {SIZE_PILLS.map(sz => (
                          <button key={sz} className={'crmcp-szpill' + (filters.company_size === sz ? ' crmcp-szpill--on' : '')} onClick={() => setFilters(p => ({ ...p, company_size: p.company_size === sz ? '' : sz }))}>{sz}</button>
                        ))}
                      </div></div>
                    <div className="crmcl-field"><label>Must include (comma separated)</label><input value={filters.include_keywords} onChange={e => setFilters(p => ({ ...p, include_keywords: e.target.value }))} /></div>
                    <div className="crmcl-field"><label>Exclude (comma separated)</label><input value={filters.exclude_keywords} onChange={e => setFilters(p => ({ ...p, exclude_keywords: e.target.value }))} /></div>
                  </div>
                  <div className="crmcp-find-bar">
                    <button className="crmcl-new" disabled={searching} onClick={() => findCompanies(false)}>{searching && !results ? 'Searching...' : 'Find companies'}</button>
                    <button className="crmcl-act" onClick={() => setFilters({ sector: str(icp.sector), company_size: str(icp.company_size), geography: str(icp.geography), include_keywords: '', exclude_keywords: '' })}>Reset to ICP</button>
                    {results && <button className="crmcl-act" onClick={() => setConfirm({ type: 'clear' })}>Clear search</button>}
                  </div>
                  {results && (
                    <div className="crmcp-results">
                      <div className="crmcp-res-bar">
                        <span className="crmcp-muted">{results.companies.length} companies{results.total_cached > 25 ? ` · ${results.total_cached} seen in total` : ''}{selected.size ? ` · ${selected.size} selected` : ''}</span>
                        <span className="crmcp-res-actions">
                          <button className="crmcl-act" disabled={searching} onClick={() => findCompanies(true)}>{searching ? 'Searching...' : 'Find more'}</button>
                          <button className="crmcl-act" onClick={() => setSelected(new Set(results.companies.filter(c => !c.already_in_workspace).slice(0, 10).map(c => c.domain)))}>Select 10</button>
                          {selected.size > 0 && <button className="crmcl-act" onClick={() => setSelected(new Set())}>Deselect</button>}
                          {selected.size > 0 && <button className="crmcl-new" disabled={importing} onClick={importSelected}>{importing ? 'Adding...' : `Add ${selected.size} to workspace`}</button>}
                        </span>
                      </div>
                      {importResult && <div className="crmcp-note-ok">{importResult.added || 0} added{importResult.skipped ? `, ${importResult.skipped} already in the workspace` : ''}.</div>}
                      {results.companies.length === 0 ? <div className="crmcp-empty">No results. Try adjusting the filters.</div> : results.companies.map(c => (
                        <div key={c.domain} className={'crmcp-co' + (selected.has(c.domain) ? ' crmcp-co--sel' : '') + (c.already_in_workspace ? ' crmcp-co--in' : '')}
                          onClick={() => { if (!c.already_in_workspace) toggle(c.domain); }}>
                          <input type="checkbox" checked={selected.has(c.domain)} disabled={c.already_in_workspace} onChange={() => {}} />
                          <div className="crmcp-co-main">
                            <div className="crmcp-co-name">{c.name}{c.already_in_workspace && <span className="crmcp-pill crmcp-pill--ok">In workspace</span>}</div>
                            <div className="crmcp-co-dom">{c.domain}</div>
                            {c.description && <div className="crmcp-co-desc">{String(c.description).replace(/^Summary:\n?/, '').slice(0, 200)}</div>}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </>
              )}
            </>
          )}
        </main>

        {/* RIGHT: Raptor */}
        <aside className="crmcp-col">
          <div className="crmcp-card crmcp-raptor">
            <div className="crmcp-rap-head"><span className="crmh-panel-mark"><img src={raptorMark} alt="Raptor" /></span><b>Raptor</b></div>

            <div className="crmcp-rap-act">
              <div className="crmcp-rap-l">Draft ICP from discovery</div>
              {discFilled === 0 && <div className="crmcp-warn">The discovery brief is empty, so the draft will be generic.</div>}
              <textarea className="crmcp-ta crmcp-ta--box" rows={2} value={icpInstr} placeholder="Optional instructions, e.g. focus on UK firms of 20 to 50 staff" onChange={e => setIcpInstr(e.target.value)} />
              <div className="crmcp-prompts">
                <button className="crmcl-act" onClick={() => setPromptsOpen(o => !o)} disabled={!prompts.length}>
                  {prompts.length ? `Saved prompts (${prompts.length})` : 'No saved prompts'}
                </button>
                <button className="crmcl-act" disabled={!icpInstr.trim()}
                  onClick={() => setPromptForm({ title: '', prompt_text: icpInstr, sector_tag: '' })}>Save as prompt</button>
                {promptsOpen && prompts.length > 0 && (
                  <div className="crmcp-prompt-menu">
                    {prompts.map(p => (
                      <div key={p.id} className="crmcp-prompt-row">
                        <button className="crmcp-prompt-pick" onClick={() => applyPrompt(p)}>
                          <span className="crmcp-prompt-title">{p.title || 'Untitled'}</span>
                          <span className="crmcp-prompt-meta">{p.sector_tag ? p.sector_tag + ' · ' : ''}used {p.use_count || 0}x</span>
                        </button>
                        <button className="crmcp-prompt-del" title="Delete prompt" onClick={() => setConfirm({ type: 'delprompt', p })}>×</button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              <button className="crmcl-new crmcp-full" disabled={drafting === 'icp'} onClick={draftIcp}>{drafting === 'icp' ? 'Drafting...' : 'Draft ICP'}</button>
            </div>

            <div className="crmcp-rap-act">
              <div className="crmcp-rap-l">Fill discovery from a description</div>
              <textarea className="crmcp-ta crmcp-ta--box" rows={3} value={discDesc} placeholder="Describe the client in plain English: who they sell to, what they tried, their sales cycle, their tone" onChange={e => setDiscDesc(e.target.value)} />
              <button className="crmcl-act crmcp-full" disabled={drafting === 'discovery' || !discDesc.trim()} onClick={fillDiscovery}>{drafting === 'discovery' ? 'Filling...' : 'Fill the brief'}</button>
            </div>

            <div className="crmcp-rap-act">
              <div className="crmcp-rap-l">Ask about this client</div>
              {messages.length > 0 && (
                <div className="crmcp-msgs" data-color-mode="light">
                  {messages.slice(-6).map((m, i) => (
                    <div key={i} className={'crmcp-msg crmcp-msg--' + m.role + (m.error ? ' crmcp-msg--err' : '')}>
                      {m.role === 'assistant' && !m.error
                        ? <MDEditor.Markdown source={m.text} />
                        : m.text}
                    </div>
                  ))}
                  {asking && <div className="crmcp-msg crmcp-msg--assistant crmcp-muted">Thinking...</div>}
                </div>
              )}
              <div className="crmcp-composer">
                <img className="crm-composer-raptor" src={raptorIcon} alt="Raptor" />
                <textarea rows={2} value={askInput} placeholder="e.g. Does the evidence back this ICP?" disabled={asking}
                  onChange={e => setAskInput(e.target.value)}
                  onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); ask(); } }} />
              </div>
            </div>
          </div>

          <div className="crmcp-card">
            <div className="crmcp-h">Stated vs evidence</div>
            <div className="crmcp-ev-row"><div className="crmcp-ev-l">Stated sector</div><div title={str(icp && icp.sector)}>{str(icp && icp.sector) || <span className="crmcp-muted">not set</span>}</div></div>
            <div className="crmcp-ev-row"><div className="crmcp-ev-l">Stated size</div><div title={str(icp && icp.company_size)}>{str(icp && icp.company_size) || <span className="crmcp-muted">not set</span>}</div></div>
            {ev.length === 0 ? <div className="crmcp-muted crmcp-ev-empty">No campaigns with an identified offering yet, so there is nothing to compare.</div>
              : ev.map(o => (
                <div key={o.offering_id} className="crmcp-ev-off">
                  <div className="crmcp-ev-name">{o.offering_label}</div>
                  <div className="crmcp-muted">{o.own_replies} graded {o.own_replies === 1 ? 'reply' : 'replies'} here, {o.own_positive} positive</div>
                  {o.evidence
                    ? <div className="crmcp-ev-stmt">Evidence: {o.evidence.statement} <span className="crmcp-pill">{o.evidence.confidence}</span></div>
                    : <div className="crmcp-muted">Not enough evidence yet to state a pattern.</div>}
                </div>
              ))}
          </div>
        </aside>
      </div>

      {promptForm && (
        <div className="crmcl-overlay" onClick={() => !saving && setPromptForm(null)}>
          <div className="crmcl-modal crmcp-modal" onClick={e => e.stopPropagation()}>
            <div className="crmcl-modal-head">
              <h2>Save as prompt</h2>
              <button className="crmcl-x" onClick={() => setPromptForm(null)}>×</button>
            </div>
            <p className="crmcp-modal-txt">Saved prompts are shared across every client, so name it for the case it fits.</p>
            <div className="crmcl-grid">
              <div className="crmcl-field crmcl-col2"><label>Title *</label>
                <input value={promptForm.title} placeholder="e.g. Accounting firms, owner-led" onChange={e => setPromptForm({ ...promptForm, title: e.target.value })} /></div>
              <div className="crmcl-field crmcl-col2"><label>Sector tag</label>
                <select value={promptForm.sector_tag} onChange={e => setPromptForm({ ...promptForm, sector_tag: e.target.value })}>
                  <option value="">No sector</option>
                  {SECTORS.map(x => <option key={x} value={x}>{x}</option>)}
                </select></div>
              <div className="crmcl-field crmcl-col2"><label>Prompt *</label>
                <textarea rows={5} value={promptForm.prompt_text} onChange={e => setPromptForm({ ...promptForm, prompt_text: e.target.value })} /></div>
            </div>
            <div className="crmcl-modal-actions">
              <button className="crmcl-btn crmcl-btn--ghost" onClick={() => setPromptForm(null)} disabled={!!saving}>Cancel</button>
              <button className="crmcl-btn crmcl-btn--primary" onClick={savePrompt} disabled={!!saving}>{saving === 'prompt' ? 'Saving...' : 'Save prompt'}</button>
            </div>
          </div>
        </div>
      )}

      {confirm && (
        <div className="crmcl-overlay" onClick={() => !saving && setConfirm(null)}>
          <div className="crmcl-modal crmcp-modal" onClick={e => e.stopPropagation()}>
            <div className="crmcl-modal-head">
              <h2>{confirm.type === 'restore' ? `Restore version ${confirm.v.version}` : confirm.type === 'delprompt' ? 'Delete saved prompt' : 'Clear search results'}</h2>
              <button className="crmcl-x" onClick={() => setConfirm(null)}>×</button>
            </div>
            <p className="crmcp-modal-txt">{confirm.type === 'restore'
              ? `Version ${confirm.v.version} is saved as a new version ${nextVersion} and becomes the active ICP. Market runs will use it. History is kept.${icpDirty.length ? ' Your unsaved ICP edits will be discarded.' : ''}`
              : confirm.type === 'delprompt'
              ? `"${confirm.p.title || 'Untitled'}" is removed from the saved prompts for every client. This cannot be undone.`
              : 'This resets the search cache for this client so the next search starts fresh.'}</p>
            <div className="crmcl-modal-actions">
              <button className="crmcl-btn crmcl-btn--ghost" onClick={() => setConfirm(null)} disabled={!!saving}>Cancel</button>
              <button className="crmcl-btn crmcl-btn--primary" disabled={!!saving}
                onClick={() => (confirm.type === 'restore' ? restoreVersion(confirm.v) : confirm.type === 'delprompt' ? deletePrompt(confirm.p) : clearSearch())}>
                {saving ? 'Working...' : confirm.type === 'restore' ? 'Restore' : confirm.type === 'delprompt' ? 'Delete' : 'Clear'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
